import dayjs from 'dayjs';
import * as yup from 'yup';
import { ScopeableType } from '../../../types';
import {
  checkDiscountOverlap,
  DiscountType,
} from '../../../utils/discount-utils';
import { packageIdFromScopes } from '../../../utils/scope-utils';
import { ScopeItem } from '../../form-fields/scope-selector/scope-selector-constants';

export const DiscountValidationSchema = yup.object({
  clientId: yup.number().required(),
  discountType: yup
    .string()
    .required()
    .typeError('Invalid Discount Type')
    .oneOf(
      [
        DiscountType.billingCaps,
        DiscountType.engagementBillingCaps,
        DiscountType.programAccess,
        DiscountType.innovationCredits,
        DiscountType.freeBillableActivities,
        DiscountType.programCapCredits,
      ],
      'Invalid Discount Type',
    ),
  startDate: yup
    .date()
    .typeError('Invalid Date')
    .required('Start date required')
    .test({
      name: 'startDateInvalid',
      skipAbsent: true,
      test(value) {
        return dayjs(value).isValid();
      },
    })
    .test({
      name: 'startDatePastDisabled',
      skipAbsent: true,
      test(value, ctx) {
        const { discountToEdit } = ctx?.options?.context ?? {};

        if (
          dayjs(value).isBefore(dayjs(), 'day') &&
          (discountToEdit
            ? dayjs(discountToEdit.startDate).isAfter(dayjs(), 'day')
            : true)
        ) {
          return ctx.createError({
            message: 'Start date is disabled before today',
          });
        }
        return true;
      },
    })
    // Date-overlap is only meaningful when a single package is targeted, so it
    // runs for the Package scope only (mirrors the old packageId-gated check).
    .when(['discountType', 'scopeableType'], (dt, schema) => {
      const [currentDiscountType, currentScopeableType] = dt;
      if (
        currentDiscountType &&
        currentScopeableType === ScopeableType.Package
      ) {
        return schema.test({
          name: 'startDateOverlaps',
          skipAbsent: true,
          test(value, ctx) {
            const { currentDiscounts, discountToEdit } =
              ctx?.options?.context ?? {};
            const { discountType, scopes, endDate } = ctx.parent;
            if (currentDiscounts.length >= 1 && value) {
              const overlapCheck = checkDiscountOverlap(
                value,
                endDate,
                discountType,
                packageIdFromScopes(scopes),
                currentDiscounts,
                discountToEdit ? discountToEdit.discountDefinitionId : null,
              );
              if (overlapCheck.startDateOverlaps) {
                return ctx.createError({
                  path: 'startDate',
                  message: 'Start date overlaps discount',
                });
              }
            }
            return true;
          },
        });
      } else {
        return schema.required('Start Date required');
      }
    }),
  endDate: yup
    .date()
    .typeError('Invalid Date')
    .required('End date required')
    .nullable()
    .when('discountType', (dt, schema) => {
      const [currentDiscountType] = dt;
      if (
        currentDiscountType === '' ||
        currentDiscountType === DiscountType.innovationCredits // nullable if innovation credits
      ) {
        return schema.nullable('true').optional();
      } else {
        return schema.required('End date required');
      }
    })
    .test({
      name: 'endDateBeforeStart',
      skipAbsent: true,
      test(value, ctx) {
        const { startDate } = ctx.parent;
        if (dayjs(value).isBefore(startDate, 'day')) {
          return ctx.createError({
            message: 'End date is before start date',
          });
        } else return true;
      },
    })
    .test({
      name: 'endDatePastDisabled',
      skipAbsent: true,
      test(value, ctx) {
        if (dayjs(value).isBefore(dayjs(), 'day') === true) {
          return ctx.createError({
            message: 'End date is disabled before today',
          });
        } else return true;
      },
    })
    .when(['discountType', 'scopeableType'], (dt, schema) => {
      const [currentDiscountType, currentScopeableType] = dt;
      if (
        currentDiscountType &&
        currentScopeableType === ScopeableType.Package
      ) {
        return schema.test({
          name: 'endDateOverlaps',
          skipAbsent: true,
          test(value, ctx) {
            const { currentDiscounts, discountToEdit } =
              ctx?.options?.context ?? {};
            const { discountType, scopes, startDate } = ctx.parent;
            if (currentDiscounts.length >= 1) {
              const overlapCheck = checkDiscountOverlap(
                startDate,
                value,
                discountType,
                packageIdFromScopes(scopes),
                currentDiscounts,
                discountToEdit ? discountToEdit.discountDefinitionId : null,
              );
              if (overlapCheck.endDateOverlaps) {
                return ctx.createError({
                  path: 'endDate',
                  message: 'End date overlaps discount',
                });
              }
            }
            return true;
          },
        });
      } else {
        return schema;
      }
    }),
  scopeableType: yup.string().oneOf(Object.values(ScopeableType)).required(),
  scopes: yup
    .array()
    .of(
      yup.object({
        scopeableType: yup
          .string()
          .oneOf(Object.values(ScopeableType))
          .required(),
        scopeableId: yup.number().nullable().required('Selection required'),
      }),
    )
    .when('scopeableType', ([type], schema) => {
      if (type === ScopeableType.Global) {
        return schema.max(0, 'Global scope cannot include selections');
      }
      return schema
        .min(1, 'Select at least one')
        .test(
          'same-type',
          'All selections must share the same scope type',
          (arr: ScopeItem[] | undefined) =>
            !arr || arr.every(s => s.scopeableType === type),
        );
    }),
  discount: yup.lazy(value =>
    value === ''
      ? yup.string().typeError('Invalid Discount').min(1).required()
      : yup
          .number()
          .typeError('Invalid Discount')
          .positive()
          .integer()
          .min(1)
          .required(),
  ),
  suppressEmptyCharges: yup.boolean().defined(),
});
